import * as angular from 'angular';
import * as _ from 'lodash';
import * as moment from 'moment';

import { IGroupService, IOnlineComputationService, ILoggerService, ISparklineService } from '../../services';
import { jobStarted, jobStarting, jobStartFailed, languageChanged, momentFormat } from '../../common/';
import { ComponentDefinition, ScriptType, GroupSchedulingViewModel, IGroupSchedulingViewModelRow, GroupSchedulingViewModelResponse } from '../../models';

class ModelSchedulingController implements angular.IComponentController {
    displayedGroups: any[];
    exposedScope: any;
    groups: any[];
    isElapsedDisplayed: boolean;
    isLoading: boolean;
    isModelOutdated: boolean;
    selectedGroup: any;
    sparkLineHeight: number;
    sparkLineWidth: number;

    private _garbages: (() => void)[] = [];

    static $inject = [
        '$scope',
        '$rootScope',
        'groupService',
        'onlineComputationService',
        'translatedLoggerService',
        'sparklineService'
    ];

    constructor(
        private _$scope: angular.IScope,
        private _$rootScope: angular.IRootScopeService,
        private _groupService: IGroupService,
        private _onlineComputationService: IOnlineComputationService,
        private _loggerService: ILoggerService,
        private _sparklineService: ISparklineService) {
    }

    $onInit() {
        this.sparkLineHeight = 20;
        this.sparkLineWidth = 150;
        this._garbages.push(this._$scope.$on(jobStarted, (_event: angular.IAngularEvent, data: GroupSchedulingViewModelResponse) => {
            this.isLoading = false;
            if (data) {
                this.updateGroupScheduling(data);
            }
            this._loggerService
                .logSuccess('Online computation started. A task was created to reprocess past dates if any.');
            this.refresh();
        }));
        this._garbages.push(this._$scope.$on(jobStartFailed, () => {
            this.isLoading = false;
            this._loggerService
                .logError('Failed to start online computation.');
        }));
        this._garbages.push(this._$scope.$on(jobStarting, () => this.isLoading = true));
        this.refresh();
        this.exposedScope = this.exposedScope || {};
        this.exposedScope.refresh = () => this.refresh();
        this._garbages.push(this._$rootScope.$on(languageChanged, () => this.refresh()));
    }

    $onChanges(changesObj: angular.IOnChangesObject) {
        if (changesObj['isElapsedDisplayed'] && changesObj['isElapsedDisplayed'].currentValue) {
            if (changesObj['isElapsedDisplayed'].currentValue === true && changesObj['isElapsedDisplayed'].previousValue === false) {
                try {
                    const filtered = this.displayedGroups?.filter(group => group.lastProcessed && !group.isIndirectlyScheduled) ?? [];
                    if (filtered && filtered.length > 0) {
                        this.asyncFillSparkLineData(filtered);
                    }
                } catch (e) {
                    console.error(e);
                }
            }
        }
    }

    $onDestroy() {
        this._garbages.forEach(w => w());
    }

    refresh() {
        this.isLoading = true;
        return this._onlineComputationService
            .getSchedulingViewModels()
            .then(data => this.updateGroupScheduling(data))
            .finally(() => this.isLoading = false);
    }

    getScriptIcon(groupViewModel: GroupSchedulingViewModel): string {
        if (groupViewModel.canExecuteScript && groupViewModel.scriptType === ScriptType.PythonV2) {
            return 'fa-code';
        } else if (groupViewModel.canExecuteScript && groupViewModel.scriptType === ScriptType.PythonV3) {
            return 'fa-code-fork';
        }
        return '';
    }

    private updateGroupScheduling(data: GroupSchedulingViewModelResponse): void {
        this.isModelOutdated = data.isModelOutdated;
        const groups: IGroupSchedulingViewModelRow[] = this._groupService.convertToTree(data.list as any[]) as any[];
        if (groups && groups.length > 0) {
            const filtered = groups?.filter(group => group.lastProcessed && group.isScheduled) ?? [];
            if (this.isElapsedDisplayed) {
                if (filtered && filtered.length > 0) {
                    this.asyncFillSparkLineData(filtered);
                }
            }
            const toFill = groups.filter(group => group.lastProcessed && !group.isIndirectlyScheduled) ?? [];
            toFill.forEach(g => {
                g.statusTitle = moment(g.lastProcessed).format(momentFormat);
            });
        }
        this.groups = groups;
    }

    private asyncFillSparkLineData(displayedGroups: IGroupSchedulingViewModelRow[]) {
        this._sparklineService
            .getSparklines(displayedGroups, this.sparkLineWidth, this.sparkLineHeight)
            .then(dictionary => {
                displayedGroups.forEach(group => {
                    if (group.isScheduled && group.guid in dictionary) {
                        group.sparkLineData = dictionary[group.guid];
                    }
                });
            });
    }
}

export const wtModelScheduling: ComponentDefinition = {
    componentId: 'wtModelScheduling',
    controller: ModelSchedulingController,
    template: require('./modelScheduling.component.html'),
    controllerAs: 'vm',
    bindings: {
        exposedScope: '=',
        hasMore: '=',
        selectedGroup: '=',
        isLoading: '<?',
        isOffline: '<',
        isModelOutdated: '=',
        isElapsedDisplayed: '<'
    }
};
